import React, { useState, useRef, useEffect } from "react";
import { useFela } from "react-fela";
import useTranslation from "next-translate/useTranslation";

import { PRICING_BACKUP_SLIDER_VALUES } from "@/constants/pricing";
import { IWithCustomStyle, useTheme } from "@/hooks/useTheme";
import { useWindowSize } from "@/hooks/useWindowResize";
import { ISlider } from "./types";

type IProps = ISlider & IWithCustomStyle & JSX.IntrinsicElements["div"];

interface IRangeValues {
  min: number;
  max: number;
  value: number;
}

export const Slider: React.FC<IProps> = ({
  onSliderChange,
  sliderValues,
  customStyle,
}) => {
  const { css } = useFela();
  const { colors } = useTheme();
  const { t } = useTranslation("section/pricingFigmaBackup");

  const { width } = useWindowSize();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const rangeRef = useRef<HTMLInputElement>(null);

  const [sliderValue, setSliderValue] = useState<number>(0);
  const [tooltipLeft, setTooltipLeft] = useState<number>(0);
  const [inputWrapperWidth, setWrapperWidth] = useState<number>(
    wrapperRef?.current?.clientWidth || 584,
  );

  const values: number[] = sliderValues.values;
  const startValue = 0;
  const endValue = values.length - 1;
  const step = 1;

  const THUMB_WIDTH = 32;
  const TOOLTIP_WIDTH = 100;
  const numberOfSteps = (endValue - startValue) / step;
  const singleRangeWidth = Math.round(inputWrapperWidth / (numberOfSteps + 1));
  const inputWidth = singleRangeWidth * numberOfSteps + THUMB_WIDTH;
  const calculateTooltipLeft = (index: number) =>
    singleRangeWidth * (index + 1) - TOOLTIP_WIDTH / 2;

  useEffect(() => {
    setWrapperWidth(wrapperRef?.current?.clientWidth || 584);
    const tooltipLeftPosition = calculateTooltipLeft(sliderValue);
    setTooltipLeft(tooltipLeftPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { min, max, value },
    } = event;

    onSliderChange(
      PRICING_BACKUP_SLIDER_VALUES[+value] || PRICING_BACKUP_SLIDER_VALUES[0],
    );
    modifyRangeInput(event, { min: +min, max: +max, value: +value });

    const tooltipLeftPosition = calculateTooltipLeft(+value);
    setTooltipLeft(tooltipLeftPosition);
  };

  const modifyRangeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    { min, max, value }: IRangeValues,
  ) => {
    setSliderValue(value);
    event.target.style.backgroundSize = `${
      ((value - min) * 100) / (max - min)
    }% 100%`;
  };

  const rangeGradientWidth = (sliderValue / numberOfSteps) * 100;
  return (
    <div
      ref={wrapperRef}
      className={css({
        textAlign: "center",
        margin: "0 20px",
        ...customStyle,
      })}
    >
      <div
        className={css({
          position: "relative",
          minHeight: 160,
          paddingTop: 105,
        })}
      >
        <div
          className={css({
            width: TOOLTIP_WIDTH,
            height: 80,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            background: colors.WHITE,
            color: colors.DARK_GREY,
            padding: "4px 12px",
            top: 2,
            left: tooltipLeft,
            boxShadow: `20px 20px 80px ${colors.CETACEAN_BLUE}`,
            borderRadius: 7,
            nested: {
              "::after": {
                content: "''",
                width: 15,
                height: 15,
                position: "absolute",
                bottom: -6,
                left: "50%",
                transform: "translateX(-50%)",
                background: "url('svgs/circled-square.svg') center no-repeat",
              },
            },
          })}
        >
          <p
            className={css({
              fontSize: 12,
              lineHeight: "16px",
              letterSpacing: 0.25,
            })}
          >
            {sliderValue === values.length - 1
              ? t("slider.over")
              : t("slider.upto")}
          </p>
          <p
            className={css({
              fontSize: 24,
              lineHeight: "32px",
              fontWeight: "bold",
            })}
          >
            {sliderValue === values.length - 1
              ? values[sliderValue - 1]
              : values[sliderValue]}
          </p>
          <p
            className={css({
              fontSize: 12,
              lineHeight: "16px",
              letterSpacing: 0.25,
            })}
          >
            {t("slider.files")}
          </p>
        </div>
        <div
          className={css({
            display: "flex",
            position: "relative",
            nested: {
              "::before": {
                content: "''",
                display: "inline-block",
                height: 6,
                width: singleRangeWidth - 4,
                borderRadius: "5px 0 0 5px",
                background: `linear-gradient(${colors.GRADIENT_PURPLE}, ${colors.PURLPE})`,
              },
            },
          })}
        >
          <div className={css({ display: "flex", position: "relative" })}>
            {values.map((_, i) => (
              <div
                key={i}
                className={css({
                  position: "absolute",
                  left: i * singleRangeWidth,
                  zIndex: 1,
                })}
              >
                {i !== values.length - 1 && (
                  <span
                    className={css({
                      display: "block",
                      width: 4,
                      height: 6,
                      backgroundColor: colors.WHITE,
                    })}
                  />
                )}
                <span
                  className={css({
                    display: "block",
                    position: "absolute",
                    left: -7,
                    marginTop: i === values.length - 1 ? 21 : 17,
                    color: colors.GRAY,
                    fontSize: 12,
                    lineHeight: "16px",
                    letterSpacing: ".25px",
                  })}
                >
                  {i === values.length - 1 ? "More" : values[i]}
                </span>
              </div>
            ))}
          </div>
          <span
            className={css({
              position: "absolute",
              height: 6,
              width: singleRangeWidth * numberOfSteps,
              background: `linear-gradient(90deg, ${colors.GRADIENT_PURPLE} ${rangeGradientWidth}%, #D1D5DB ${rangeGradientWidth}%)`,
              borderRadius: "0 5px 5px 0",
              left: singleRangeWidth,
            })}
          />
          <input
            ref={rangeRef}
            type="range"
            min={startValue}
            defaultValue={startValue}
            max={endValue}
            step={step}
            onChange={handleInputChange}
            className={css({
              width: inputWidth,
              position: "absolute",
              left: singleRangeWidth - THUMB_WIDTH / 2,
              cursor: "pointer",
              zIndex: 1,
              margin: 0,
              background: "transparent",
              nested: {
                '[type="range"]': {
                  height: 6,
                  appearance: "none",
                },
                '[type="range"]::-webkit-slider-thumb': {
                  appearance: "none",
                  width: THUMB_WIDTH,
                  height: THUMB_WIDTH,
                  borderRadius: "50%",
                  background: `url('svgs/move-icon.svg') ${colors.GRADIENT_PURPLE} center no-repeat`,
                },
              },
            })}
          />
        </div>
      </div>
    </div>
  );
};
